import axios, { type AxiosRequestConfig } from "axios";

import { getSelectedGroupId } from "@/script/group_sync";
import {
  trackHttpAction,
  type ActionTrackConfig,
} from "@/service/actionTracking";
import { useGroupStore } from "@/store/groupStore";

declare module "axios" {
  interface AxiosRequestConfig {
    readonly actionTrack?: ActionTrackConfig;
    readonly skipGroupIdInjection?: boolean;
  }
}

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
});

// 선택 병동(group_id) 자동 주입.
// 백엔드가 토큰 대신 요청의 group_id 로 그룹 스코프를 해석하므로(docs/FRONTEND_GROUP_ID_MIGRATION.md),
// 화면에서 선택한 병동을 모든 요청 쿼리에 실어 보낸다. 미선택 시엔 보내지 않아 백엔드 home 폴백에 맡긴다.
// "undefined"/"null"/빈값 같은 무효 문자열은 절대 보내지 않는다(과거 403 원인).
const INVALID_GROUP_ID = new Set(["", "undefined", "null", "NaN"]);
function normalizeGroupId(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  return v && !INVALID_GROUP_ID.has(v) ? v : null;
}

// 선택 병동의 권위 소스 = zustand groupStore(드롭다운/useInitializeGroup 이 채움).
// flat localStorage 키(getSelectedGroupId)는 레거시 폴백.
function currentSelectedGroupId(): string | null {
  const fromStore = normalizeGroupId(useGroupStore.getState().selectedGroupId);
  if (fromStore) return fromStore;
  return normalizeGroupId(getSelectedGroupId());
}

axiosInstance.interceptors.request.use((config) => {
  // 1) URL 문자열에 잘못 박힌 group_id=undefined|null 제거(레거시 호출부 방어)
  if (config.url) {
    config.url = config.url
      .replace(/&group_id=(?:undefined|null)\b/g, "")
      .replace(/\?group_id=(?:undefined|null)&/g, "?")
      .replace(/\?group_id=(?:undefined|null)\b/g, "");
  }

  // 2) params.group_id 정규화 + 선택 병동 주입
  const urlHasGroupId =
    !!config.url && /[?&]group_id=[^&]+/.test(config.url);
  const params = (config.params ?? {}) as Record<string, unknown>;
  const current = normalizeGroupId(params.group_id);

  if ("group_id" in params && !current) {
    delete params.group_id; // 무효값 제거 (axios 가 "undefined" 직렬화하지 않도록)
  }
  // 호출부가 명시한 유효 group_id(쿼리스트링 포함)가 있으면 존중, 없을 때만 선택값 주입
  if (!config.skipGroupIdInjection && !current && !urlHasGroupId) {
    const selected = currentSelectedGroupId();
    if (selected) params.group_id = selected;
  }
  if (Object.keys(params).length > 0) config.params = params;

  return config;
});

function stringifyUnknown(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function extractApiErrorMessage(data: unknown): string {
  if (data == null) return "서버 에러가 발생했습니다.";
  if (typeof data === "string") return data;
  if (typeof data !== "object") return stringifyUnknown(data);

  const obj = data as {
    detail?: unknown;
    message?: unknown;
    errors?: unknown;
  };

  const details = obj.detail ?? obj.message ?? obj.errors;
  if (Array.isArray(details)) {
    const joined = details
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") {
          const rec = d as {
            detail?: unknown;
            msg?: unknown;
            message?: unknown;
          };
          return stringifyUnknown(rec.detail ?? rec.msg ?? rec.message ?? d);
        }
        return stringifyUnknown(d);
      })
      .filter(Boolean)
      .join("\n");
    return joined || "서버 에러가 발생했습니다.";
  }

  // 도메인 422 는 detail 이 {code, message} 객체이거나 {errors:[{message}]} 중첩이다.
  // 그대로 stringify 하면 사용자에게 `{"code":"...","message":"..."}` 가 그대로 보인다.
  if (details && typeof details === "object" && !Array.isArray(details)) {
    const rec = details as {
      message?: unknown;
      detail?: unknown;
      errors?: unknown;
    };
    const inner = rec.message ?? rec.detail;
    if (typeof inner === "string" && inner) return inner;
    if (Array.isArray(rec.errors)) {
      const joined = rec.errors
        .map((e) =>
          e && typeof e === "object"
            ? stringifyUnknown((e as { message?: unknown }).message ?? e)
            : stringifyUnknown(e)
        )
        .filter(Boolean)
        .join("\n");
      if (joined) return joined;
    }
  }

  if (details !== undefined) {
    return stringifyUnknown(details) || "서버 에러가 발생했습니다.";
  }

  return "서버 에러가 발생했습니다.";
}

function headerToString(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.join(",");
  return "";
}

axiosInstance.interceptors.response.use(
  (response) => {
    // HTML 응답 체크 (서버 에러 페이지 등) - 404처럼 처리, toast 표시 안 함
    const contentType = headerToString(response.headers["content-type"]);
    if (contentType.includes("text/html")) {
      console.warn(
        "[Axios] Received HTML response instead of JSON, treating as 404"
      );
      const error = new Error("Server returned HTML instead of JSON");
      const axiosError = error as {
        response?: { status?: number };
        isAxiosError?: boolean;
        skipToast?: boolean;
      };
      axiosError.response = { status: 404 }; // 404로 처리하여 폴링에서 null 반환
      axiosError.isAxiosError = true;
      axiosError.skipToast = true; // toast 표시 안 함
      return Promise.reject(axiosError);
    }

    trackHttpAction(response.config, "success", response.status);
    return response.data;
  },
  (error) => {
    if (axios.isAxiosError(error)) {
      trackHttpAction(error.config, "failure", error.response?.status);

      // HTML 응답 체크 (에러 케이스) - toast 표시 안 함
      const contentType = headerToString(
        error.response?.headers?.["content-type"]
      );
      if (contentType.includes("text/html")) {
        console.warn(
          "[Axios] Received HTML response in error case, treating as 404"
        );
        const htmlError = error as {
          response?: { status?: number };
          isAxiosError?: boolean;
          message?: string;
          skipToast?: boolean;
        };
        htmlError.response = { status: 404 };
        htmlError.isAxiosError = true;
        htmlError.message = "서버가 HTML을 반환했습니다.";
        htmlError.skipToast = true; // toast 표시 안 함
        return Promise.reject(htmlError);
      }

      const message = extractApiErrorMessage(error.response?.data);

      // 에러 메시지를 error 객체에 추가
      error.message = stringifyUnknown(message) || "서버 에러가 발생했습니다.";
    } else {
      error.message = "알 수 없는 에러가 발생했습니다.";
    }

    return Promise.reject(error);
  }
);

const httpClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.get<T, T>(url, config),
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.post<T, T>(url, data, config),
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.put<T, T>(url, data, config),
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.patch<T, T>(url, data, config),
  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.delete<T, T>(url, config),
};

export default httpClient;
